// src/hooks/provider.tsx
import { useEffect, useMemo, useRef } from "react";

// src/hooks/context.ts
import { createContext } from "react";
var PreferencesContext = createContext(null);
PreferencesContext.displayName = "PreferencesContext";
function makeLoadingEntry(existingEntry) {
  return {
    document: existingEntry?.document ?? null,
    resolvedData: existingEntry?.resolvedData ?? {},
    provenance: existingEntry?.provenance ?? null,
    state: "LOADING" /* LOADING */,
    error: null,
    optimisticData: existingEntry?.optimisticData ?? null
  };
}
function applyStoreAction(documents, action) {
  switch (action.type) {
    case "SET_DOCUMENT": {
      const next = new Map(documents);
      next.set(action.key, action.entry);
      return next;
    }
    case "SET_LOADING": {
      const existing = documents.get(action.key);
      const next = new Map(documents);
      next.set(action.key, makeLoadingEntry(existing));
      return next;
    }
    case "SET_ERROR": {
      const existing = documents.get(action.key);
      const next = new Map(documents);
      const errorEntry = {
        document: existing?.document ?? null,
        resolvedData: existing?.resolvedData ?? {},
        provenance: existing?.provenance ?? null,
        state: "STALE" /* STALE */,
        error: action.error,
        optimisticData: null
      };
      next.set(action.key, errorEntry);
      return next;
    }
    case "SET_OPTIMISTIC": {
      const existing = documents.get(action.key);
      if (!existing) {
        return documents;
      }
      const next = new Map(documents);
      next.set(action.key, {
        ...existing,
        optimisticData: action.data,
        state: "OPTIMISTIC" /* OPTIMISTIC */,
        error: null
      });
      return next;
    }
    case "CLEAR_OPTIMISTIC": {
      const existing = documents.get(action.key);
      if (!existing) {
        return documents;
      }
      const next = new Map(documents);
      next.set(action.key, {
        ...existing,
        optimisticData: null,
        // Revert to SYNCED if we have server-authoritative data, else STALE.
        state: existing.document !== null ? "SYNCED" /* SYNCED */ : "STALE" /* STALE */
      });
      return next;
    }
    case "CLEAR_ALL":
      return /* @__PURE__ */ new Map();
    default: {
      const _exhaustive = action;
      return documents;
    }
  }
}
function makeDefaultEntry(defaults = {}) {
  return {
    document: null,
    resolvedData: defaults,
    provenance: null,
    state: "DEFAULTS_ONLY" /* DEFAULTS_ONLY */,
    error: null,
    optimisticData: null
  };
}
function makeResolvedEntry(document, resolvedData, provenance) {
  return {
    document,
    resolvedData,
    provenance,
    state: "SYNCED" /* SYNCED */,
    error: null,
    optimisticData: null
  };
}
function makeCachedEntry(document) {
  return {
    document,
    resolvedData: document.data,
    provenance: null,
    state: "CACHED" /* CACHED */,
    error: null,
    optimisticData: null
  };
}
function needsFetch(entry) {
  if (!entry) return true;
  return entry.state === "DEFAULTS_ONLY" /* DEFAULTS_ONLY */ || entry.state === "CACHED" /* CACHED */ || entry.state === "STALE" /* STALE */ || entry.state === "CONFLICT" /* CONFLICT */;
}
function serializeKey(identity) {
  return `${identity.domain}:${identity.contextType}:${identity.contextKey}:${identity.documentFamily}`;
}

// src/hooks/provider.tsx
import { jsx } from "react/jsx-runtime";
function createStore(transport, cache, config) {
  let documents = /* @__PURE__ */ new Map();
  const keyedListeners = /* @__PURE__ */ new Map();
  const wildcardListeners = /* @__PURE__ */ new Set();
  function notifyKey(key) {
    const listeners = keyedListeners.get(key);
    if (listeners) {
      for (const listener of listeners) {
        listener();
      }
    }
    for (const listener of wildcardListeners) {
      listener();
    }
  }
  function notifyAll() {
    for (const key of documents.keys()) {
      const listeners = keyedListeners.get(key);
      if (listeners) {
        for (const listener of listeners) {
          listener();
        }
      }
    }
    for (const listener of wildcardListeners) {
      listener();
    }
  }
  const store = {
    get documents() {
      return documents;
    },
    transport,
    cache,
    config,
    subscribe(key, listener) {
      if (key === "*") {
        wildcardListeners.add(listener);
        return () => {
          wildcardListeners.delete(listener);
        };
      }
      let set = keyedListeners.get(key);
      if (!set) {
        set = /* @__PURE__ */ new Set();
        keyedListeners.set(key, set);
      }
      set.add(listener);
      return () => {
        const s = keyedListeners.get(key);
        if (s) {
          s.delete(listener);
          if (s.size === 0) {
            keyedListeners.delete(key);
          }
        }
      };
    },
    getEntry(key) {
      return documents.get(key);
    },
    dispatch(action) {
      if (action.type === "CLEAR_ALL") {
        const prevDocs = documents;
        documents = applyStoreAction(documents, action);
        for (const key of prevDocs.keys()) {
          const listeners = keyedListeners.get(key);
          if (listeners) {
            for (const listener of listeners) {
              listener();
            }
          }
        }
        for (const listener of wildcardListeners) {
          listener();
        }
      } else {
        documents = applyStoreAction(documents, action);
        notifyKey(action.key);
      }
    }
  };
  return store;
}
async function hydrateFromCache(store, key) {
  try {
    const cacheEntry = await store.cache.get(key);
    if (!cacheEntry) return false;
    if (cacheEntry.expiresAt !== null) {
      if (Date.now() > new Date(cacheEntry.expiresAt).getTime()) {
        await store.cache.delete(key).catch(() => void 0);
        return false;
      }
    }
    store.dispatch({
      type: "SET_DOCUMENT",
      key,
      entry: makeCachedEntry(cacheEntry.document)
    });
    return true;
  } catch {
    return false;
  }
}
async function computeChecksum(data) {
  const json = JSON.stringify(data);
  if (typeof crypto !== "undefined" && typeof crypto.subtle !== "undefined") {
    const encoded = new TextEncoder().encode(json);
    const hashBuffer = await crypto.subtle.digest("SHA-256", encoded);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  }
  let hash = 5381;
  for (let i = 0; i < json.length; i++) {
    hash = (hash << 5) + hash ^ json.charCodeAt(i);
  }
  return (hash >>> 0).toString(16);
}
async function runBootstrap(store, domains) {
  try {
    const cachedKeys = await store.cache.keys();
    const domainSet = new Set(domains);
    const hydratePromises = [];
    for (const key of cachedKeys) {
      const keyStr = key;
      const domainPart = keyStr.split(":")[0] ?? "";
      if (domainSet.has(domainPart)) {
        hydratePromises.push(hydrateFromCache(store, keyStr));
      }
    }
    await Promise.all(hydratePromises);
  } catch {
  }
  let bootstrapResult;
  try {
    bootstrapResult = await store.transport.bootstrap({
      domains,
      includeProvenance: true
    });
  } catch {
    return;
  }
  const now = /* @__PURE__ */ new Date();
  const ttlMs = store.config.defaultCacheTtlMs ?? 24 * 60 * 60 * 1e3;
  const expiresAt = new Date(now.getTime() + ttlMs).toISOString();
  const cachedAt = now.toISOString();
  const effectiveByKey = /* @__PURE__ */ new Map();
  for (const eff of bootstrapResult.effectiveDocuments) {
    effectiveByKey.set(serializeKey(eff.identity), eff);
  }
  for (const rawDoc of bootstrapResult.documents) {
    const doc = rawDoc;
    const key = serializeKey(doc.identity);
    const effective = effectiveByKey.get(key);
    const resolvedData = effective ? effective.data : doc.data;
    const provenance = effective ? effective.provenance : null;
    const storeEntry = makeResolvedEntry(doc, resolvedData, provenance);
    store.dispatch({ type: "SET_DOCUMENT", key, entry: storeEntry });
    try {
      const checksum = await computeChecksum(resolvedData);
      await store.cache.set(key, {
        document: doc,
        cachedAt,
        expiresAt,
        checksum
      });
    } catch {
    }
  }
}
function PreferencesProvider({
  config,
  children
}) {
  const storeRef = useRef(null);
  if (storeRef.current === null) {
    storeRef.current = createStore(config.transport, config.cache, config);
  }
  const store = storeRef.current;
  useEffect(() => {
    const domains = config.bootstrapDomains;
    if (!domains || domains.length === 0) return;
    let cancelled = false;
    void runBootstrap(store, domains).catch(() => {
      if (!cancelled) {
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const contextValue = useMemo(() => store, [store]);
  return /* @__PURE__ */ jsx(PreferencesContext.Provider, { value: contextValue, children });
}

// src/hooks/use-store.ts
import { useContext } from "react";
function useStore() {
  const store = useContext(PreferencesContext);
  if (store === null) {
    throw new Error(
      "[user-preferences] useStore: No PreferencesProvider found in the component tree. Wrap your application (or the subtree that uses preference hooks) with <PreferencesProvider config={...}>...</PreferencesProvider>."
    );
  }
  return store;
}

// src/hooks/use-preferences.ts
function usePreferences() {
  return useStore();
}

// src/hooks/use-preference.ts
import { useCallback, useEffect as useEffect2, useSyncExternalStore } from "react";
var EMPTY_DOCUMENT = {};
function getDocumentData(store, key) {
  const entry = store.documents.get(key);
  if (!entry) return EMPTY_DOCUMENT;
  return entry.optimisticData ?? entry.resolvedData;
}
function entryToMeta(entry) {
  if (!entry) {
    return {
      state: "DEFAULTS_ONLY" /* DEFAULTS_ONLY */,
      loading: false,
      stale: false,
      error: null
    };
  }
  return {
    state: entry.state,
    loading: entry.state === "LOADING" /* LOADING */,
    stale: entry.state === "STALE" /* STALE */,
    error: entry.error
  };
}
function usePreference(identity) {
  const store = useStore();
  const key = serializeKey(identity);
  const subscribe = useCallback(
    (onStoreChange) => store.subscribe(key, onStoreChange),
    [store, key]
  );
  const getSnapshot = useCallback(
    () => getDocumentData(store, key),
    [store, key]
  );
  const data = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  useEffect2(() => {
    let cancelled = false;
    const entry = store.getEntry(key);
    if (!needsFetch(entry)) return;
    const fetch = async () => {
      store.dispatch({ type: "SET_LOADING", key });
      try {
        const resolved = await store.transport.fetchEffective({
          identity,
          includeProvenance: true
        });
        if (cancelled) return;
        if (!resolved) {
          store.dispatch({ type: "SET_DOCUMENT", key, entry: makeDefaultEntry() });
        } else {
          store.dispatch({
            type: "SET_DOCUMENT",
            key,
            entry: makeResolvedEntry(
              null,
              resolved.data,
              resolved.provenance
            )
          });
        }
      } catch (err) {
        if (!cancelled) {
          store.dispatch({
            type: "SET_ERROR",
            key,
            error: err instanceof Error ? err : new Error(String(err))
          });
        }
      }
    };
    void fetch();
    return () => {
      cancelled = true;
    };
  }, [store, key]);
  const meta = entryToMeta(store.getEntry(key));
  return {
    data,
    state: meta.state,
    loading: meta.loading,
    stale: meta.stale,
    error: meta.error
  };
}

// src/hooks/use-preference-document.ts
import { useCallback as useCallback2, useEffect as useEffect3, useRef as useRef2, useSyncExternalStore as useSyncExternalStore2 } from "react";
var EMPTY_DOCUMENT_STATE = Object.freeze({
  data: Object.freeze({}),
  document: null,
  provenance: null,
  state: "DEFAULTS_ONLY" /* DEFAULTS_ONLY */,
  loading: false,
  stale: false,
  error: null
});
function makeEntrySnapshot(entry) {
  if (!entry) {
    return EMPTY_DOCUMENT_STATE;
  }
  const effectiveData = entry.optimisticData ?? entry.resolvedData;
  return {
    data: effectiveData,
    document: entry.document,
    provenance: entry.provenance,
    state: entry.state,
    loading: entry.state === "LOADING" /* LOADING */,
    stale: entry.state === "STALE" /* STALE */,
    error: entry.error
  };
}
function usePreferenceDocument(identity) {
  const store = useStore();
  const key = serializeKey(identity);
  const lastEntryRef = useRef2(void 0);
  const lastSnapshotRef = useRef2(EMPTY_DOCUMENT_STATE);
  const subscribe = useCallback2(
    (onStoreChange) => store.subscribe(key, onStoreChange),
    [store, key]
  );
  const getSnapshot = useCallback2(() => {
    const currentEntry = store.getEntry(key);
    if (currentEntry === lastEntryRef.current) {
      return lastSnapshotRef.current;
    }
    const snapshot = makeEntrySnapshot(currentEntry);
    lastEntryRef.current = currentEntry;
    lastSnapshotRef.current = snapshot;
    return snapshot;
  }, [store, key]);
  const documentState = useSyncExternalStore2(subscribe, getSnapshot, getSnapshot);
  useEffect3(() => {
    let cancelled = false;
    const entry = store.getEntry(key);
    if (!needsFetch(entry)) return;
    const fetch = async () => {
      store.dispatch({ type: "SET_LOADING", key });
      try {
        const resolved = await store.transport.fetchEffective({
          identity,
          includeProvenance: true
        });
        if (cancelled) return;
        if (!resolved) {
          store.dispatch({ type: "SET_DOCUMENT", key, entry: makeDefaultEntry() });
          return;
        }
        let rawDocument = null;
        try {
          rawDocument = await store.transport.fetchDocument({ identity });
        } catch {
        }
        if (cancelled) return;
        store.dispatch({
          type: "SET_DOCUMENT",
          key,
          entry: makeResolvedEntry(
            rawDocument,
            resolved.data,
            resolved.provenance
          )
        });
      } catch (err) {
        if (!cancelled) {
          store.dispatch({
            type: "SET_ERROR",
            key,
            error: err instanceof Error ? err : new Error(String(err))
          });
        }
      }
    };
    void fetch();
    return () => {
      cancelled = true;
    };
  }, [store, key]);
  return documentState;
}

// src/hooks/use-preference-patch.ts
import { useCallback as useCallback3 } from "react";
async function computeChecksum2(data) {
  const json = JSON.stringify(data);
  if (typeof crypto !== "undefined" && typeof crypto.subtle !== "undefined") {
    const encoded = new TextEncoder().encode(json);
    const hashBuffer = await crypto.subtle.digest("SHA-256", encoded);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  }
  let hash = 5381;
  for (let i = 0; i < json.length; i++) {
    hash = (hash << 5) + hash ^ json.charCodeAt(i);
  }
  return (hash >>> 0).toString(16);
}
async function writeThroughCache(store, key, document, effectiveData) {
  const ttlMs = store.config.defaultCacheTtlMs ?? 24 * 60 * 60 * 1e3;
  const now = /* @__PURE__ */ new Date();
  const expiresAt = new Date(now.getTime() + ttlMs).toISOString();
  try {
    const checksum = await computeChecksum2(effectiveData);
    await store.cache.set(key, {
      document,
      cachedAt: now.toISOString(),
      expiresAt,
      checksum
    });
  } catch {
  }
}
async function performRefetch(store, identity, key) {
  store.dispatch({ type: "SET_LOADING", key });
  try {
    const resolved = await store.transport.fetchEffective({
      identity,
      includeProvenance: true
    });
    if (!resolved) {
      store.dispatch({ type: "SET_DOCUMENT", key, entry: makeDefaultEntry() });
      return;
    }
    let rawDocument = null;
    try {
      rawDocument = await store.transport.fetchDocument({ identity });
    } catch {
    }
    const entry = makeResolvedEntry(
      rawDocument,
      resolved.data,
      resolved.provenance
    );
    store.dispatch({ type: "SET_DOCUMENT", key, entry });
    if (rawDocument) {
      await writeThroughCache(
        store,
        key,
        rawDocument,
        resolved.data
      );
    }
  } catch (err) {
    store.dispatch({
      type: "SET_ERROR",
      key,
      error: err instanceof Error ? err : new Error(String(err))
    });
  }
}
function usePreferencePatch(identity, scope = "USER_OVERRIDE" /* USER_OVERRIDE */) {
  const store = useStore();
  const key = serializeKey(identity);
  const patch = useCallback3(
    async (data) => {
      const currentEntry = store.getEntry(key);
      const currentData = currentEntry?.optimisticData ?? currentEntry?.resolvedData ?? {};
      const optimisticData = { ...currentData, ...data };
      store.dispatch({
        type: "SET_OPTIMISTIC",
        key,
        data: optimisticData
      });
      const expectedRevision = currentEntry?.document?.revision ?? null;
      let result;
      try {
        result = await store.transport.patch({
          identity,
          scope,
          data,
          expectedRevision
        });
      } catch (err) {
        store.dispatch({ type: "CLEAR_OPTIMISTIC", key });
        const error = err instanceof Error ? err : new Error(String(err));
        store.dispatch({ type: "SET_ERROR", key, error });
        return {
          kind: "validation_error",
          errors: [
            {
              path: "",
              code: "NETWORK_ERROR",
              message: error.message
            }
          ]
        };
      }
      switch (result.kind) {
        case "success": {
          const successEntry = {
            document: result.document,
            resolvedData: result.effectiveData,
            provenance: null,
            // Provenance not returned by patch — refetch if needed.
            state: "SYNCED" /* SYNCED */,
            error: null,
            optimisticData: null
          };
          store.dispatch({ type: "SET_DOCUMENT", key, entry: successEntry });
          await writeThroughCache(
            store,
            key,
            result.document,
            result.effectiveData
          );
          return result;
        }
        case "conflict": {
          store.dispatch({ type: "CLEAR_OPTIMISTIC", key });
          const currentEntryAfterClear = store.getEntry(key);
          if (currentEntryAfterClear) {
            store.dispatch({
              type: "SET_DOCUMENT",
              key,
              entry: {
                ...currentEntryAfterClear,
                state: "CONFLICT" /* CONFLICT */,
                error: new Error(
                  `Revision conflict: expected ${result.expectedRevision}, got ${result.actualRevision}`
                ),
                optimisticData: null
              }
            });
          }
          void performRefetch(store, identity, key);
          return result;
        }
        case "validation_error": {
          store.dispatch({ type: "CLEAR_OPTIMISTIC", key });
          const validationError = new Error(
            result.errors.map((e) => `${e.path}: ${e.message}`).join("; ")
          );
          store.dispatch({ type: "SET_ERROR", key, error: validationError });
          return result;
        }
        default: {
          const _exhaustive = result;
          return _exhaustive;
        }
      }
    },
    [store, key, identity, scope]
  );
  const reset = useCallback3(
    async (resetScope) => {
      const targetScope = resetScope ?? scope;
      store.dispatch({ type: "SET_LOADING", key });
      try {
        const result = await store.transport.reset({ identity, scope: targetScope });
        if (result.kind === "success") {
          await performRefetch(store, identity, key);
        } else if (result.kind === "conflict") {
          store.dispatch({
            type: "SET_ERROR",
            key,
            error: new Error(
              `Reset conflict: expected ${result.expectedRevision}, got ${result.actualRevision}`
            )
          });
          void performRefetch(store, identity, key);
        } else {
          const validationError = new Error(
            result.errors.map((e) => `${e.path}: ${e.message}`).join("; ")
          );
          store.dispatch({ type: "SET_ERROR", key, error: validationError });
        }
        return result;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        store.dispatch({ type: "SET_ERROR", key, error });
        return {
          kind: "validation_error",
          errors: [{ path: "", code: "NETWORK_ERROR", message: error.message }]
        };
      }
    },
    [store, key, identity, scope]
  );
  const refetch = useCallback3(async () => {
    await performRefetch(store, identity, key);
  }, [store, identity, key]);
  return { patch, reset, refetch };
}

// src/hooks/use-workspace-preferences.ts
import { useMemo as useMemo2 } from "react";
function useWorkspacePreferences(domain, workspaceKey, documentFamily, scope) {
  const identity = useMemo2(
    () => ({
      domain,
      contextType: "WORKSPACE" /* WORKSPACE */,
      contextKey: workspaceKey,
      documentFamily
    }),
    [domain, workspaceKey, documentFamily]
  );
  const documentState = usePreferenceDocument(identity);
  const patchActions = usePreferencePatch(identity, scope);
  return useMemo2(
    () => ({
      ...documentState,
      patch: patchActions.patch,
      reset: patchActions.reset,
      refetch: patchActions.refetch
    }),
    [documentState, patchActions]
  );
}

// src/hooks/use-resolved-preference.ts
import { useCallback as useCallback4, useEffect as useEffect4, useRef as useRef3, useSyncExternalStore as useSyncExternalStore3 } from "react";
var EMPTY_RESOLVED_STATE = Object.freeze({
  data: Object.freeze({}),
  provenance: null,
  loading: false,
  stale: false,
  error: null
});
function buildResolvedState(entry) {
  if (!entry) {
    return EMPTY_RESOLVED_STATE;
  }
  return {
    data: entry.optimisticData ?? entry.resolvedData,
    provenance: entry.provenance,
    loading: entry.state === "LOADING" /* LOADING */,
    stale: entry.state === "STALE" /* STALE */ || entry.state === "CACHED" /* CACHED */ || entry.state === "DEGRADED" /* DEGRADED */,
    error: entry.error
  };
}
function useResolvedPreference(identity) {
  const store = useStore();
  const key = serializeKey(identity);
  const lastEntryRef = useRef3(void 0);
  const lastSnapshotRef = useRef3(
    EMPTY_RESOLVED_STATE
  );
  const subscribe = useCallback4(
    (onStoreChange) => store.subscribe(key, onStoreChange),
    [store, key]
  );
  const getSnapshot = useCallback4(() => {
    const currentEntry = store.getEntry(key);
    if (currentEntry === lastEntryRef.current) {
      return lastSnapshotRef.current;
    }
    const snapshot = buildResolvedState(currentEntry);
    lastEntryRef.current = currentEntry;
    lastSnapshotRef.current = snapshot;
    return snapshot;
  }, [store, key]);
  const state = useSyncExternalStore3(subscribe, getSnapshot, getSnapshot);
  useEffect4(() => {
    let cancelled = false;
    const entry = store.getEntry(key);
    if (!needsFetch(entry)) return;
    const fetch = async () => {
      store.dispatch({ type: "SET_LOADING", key });
      try {
        const resolved = await store.transport.fetchEffective({
          identity,
          includeProvenance: true
        });
        if (cancelled) return;
        if (!resolved) {
          store.dispatch({ type: "SET_DOCUMENT", key, entry: makeDefaultEntry() });
          return;
        }
        store.dispatch({
          type: "SET_DOCUMENT",
          key,
          entry: makeResolvedEntry(
            null,
            resolved.data,
            resolved.provenance
          )
        });
      } catch (err) {
        if (!cancelled) {
          store.dispatch({
            type: "SET_ERROR",
            key,
            error: err instanceof Error ? err : new Error(String(err))
          });
        }
      }
    };
    void fetch();
    return () => {
      cancelled = true;
    };
  }, [store, key]);
  return state;
}

export {
  PreferencesContext,
  PreferencesProvider,
  usePreferences,
  usePreference,
  usePreferenceDocument,
  usePreferencePatch,
  useWorkspacePreferences,
  useResolvedPreference
};
//# sourceMappingURL=chunk-BBSCP7OB.js.map